import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  FileText,
  Loader2,
  PackageCheck,
  Printer,
  RefreshCw,
  Save,
  Scissors,
} from "lucide-react";
import { toast } from "react-toastify";

import {
  getAdminOrderByNumber,
  resetAdminOrderError,
  resetAdminOrderMeta,
  updateAdminManualPaymentStatus,
} from "../../../actions/adminOrderActions";
import Meta from "../../../components/Meta";
import {
  formatDate,
  formatMoney,
  getCustomerName,
  getLineMeta,
  getLineQuantity,
  getLineTitle,
  getOrderCurrency,
  getOrderItems,
  getOrderNumber,
  getOrderStatusLabel,
  getOrderStatusTone,
  getPaymentLabel,
  getPaymentStatusLabel,
  getPaymentStatusTone,
  getFulfillmentStatusLabel,
  getFulfillmentStatusTone,
  getPrimaryAddress,
  humanize,
  normalizeLower,
  safeText,
} from "../orderViewUtils";
import { getSafeUserMessage } from "../../../utils/errorMessages";

import "./AdminOrderManagementScreen.css";
import AdminOrderFinancialActions, { hasFinancialPermission } from "./AdminOrderFinancialActions";
import AdminOrderOperationalActions from "./AdminOrderOperationalActions";
import OrderTimeline from "../OrderTimeline";
const PAYMENT_STATUSES = [
  "not_started",
  "requires_payment_method",
  "requires_action",
  "processing",
  "paid",
  "failed",
];

function DetailCard({ label, value, note }) {
  return (
    <div className="admin-order-manage__summary-card">
      <span>{label}</span>
      <strong>{value}</strong>
      {note ? <small>{note}</small> : null}
    </div>
  );
}

function StatusBadge({ value, fallback = "Pending", type = "order" }) {
  const label =
    type === "payment"
      ? getPaymentStatusLabel(value)
      : type === "fulfillment"
        ? getFulfillmentStatusLabel(value)
        : getOrderStatusLabel(value) || fallback;

  const tone =
    type === "payment"
      ? getPaymentStatusTone(value)
      : type === "fulfillment"
        ? getFulfillmentStatusTone(value)
        : getOrderStatusTone(value || fallback);

  return (
    <span className={`admin-order-manage__badge is-${tone}`}>
      {label}
    </span>
  );
}

function FieldSelect({ label, value, options, onChange, disabled }) {
  return (
    <label className="admin-order-manage__field">
      <span>{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      >
        {!options.includes(value) ? <option value={value} disabled>{humanize(value)}</option> : null}
        {options.map((item) => (
          <option key={item} value={item}>
            {humanize(item)}
          </option>
        ))}
      </select>
    </label>
  );
}

function getLineImage(item = {}) {
  return safeText(item.image || item.productImage || item.defaultImage);
}

function ReceiptSummary({ order }) {
  const currency = getOrderCurrency(order);

  return (
    <section className="admin-order-manage__panel">
      <header>
        <div>
          <h2>Receipt summary</h2>
          <p>Totals captured on the customer order.</p>
        </div>
      </header>
      <div className="admin-order-manage__totals">
        <div>
          <span>Subtotal</span>
          <strong>{formatMoney(order.subtotal, currency)}</strong>
        </div>
        <div>
          <span>Shipping</span>
          <strong>{formatMoney(order.shippingTotal, currency)}</strong>
        </div>
        <div>
          <span>Tax</span>
          <strong>{formatMoney(order.taxTotal, currency)}</strong>
        </div>
        <div>
          <span>
            {order.promoCode?.code
              ? `Discount (${order.promoCode.code})`
              : "Discount"}
          </span>
          <strong>-{formatMoney(order.discountTotal, currency)}</strong>
        </div>
        <div className="admin-order-manage__total-row">
          <span>Total</span>
          <strong>{formatMoney(order.grandTotal, currency)}</strong>
        </div>
      </div>
    </section>
  );
}

function OrderLines({ order }) {
  const currency = getOrderCurrency(order);
  const items = getOrderItems(order);

  return (
    <section className="admin-order-manage__panel admin-order-manage__panel--wide">
      <header>
        <div>
          <h2>Order lines</h2>
          <p>Items, variants, quantities and line totals.</p>
        </div>
      </header>

      {items.length ? (
        <div className="admin-order-manage__table-wrap">
          <table className="admin-order-manage__table">
            <thead>
              <tr>
                <th>Item</th>
                <th>SKU</th>
                <th>Qty</th>
                <th>Cut</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id || item._id || `${item.productName}-${item.sku}`}>
                  <td>
                    <div className="admin-order-manage__line-product">
                      {getLineImage(item) ? (
                        <img
                          src={getLineImage(item)}
                          alt=""
                          className="admin-order-manage__line-image"
                        />
                      ) : (
                        <span className="admin-order-manage__line-image-placeholder">
                          IMAGE
                        </span>
                      )}
                      <div>
                        <strong>{getLineTitle(item)}</strong>
                        <small>{getLineMeta(item) || "Academy Costumes London"}</small>
                      </div>
                    </div>
                  </td>
                  <td>{item.variantSku || item.sku || "-"}</td>
                  <td>{getLineQuantity(item)}{item.statutoryCancelledQty > 0 ? <small>{item.statutoryCancelledQty} cancelled before dispatch</small> : null}</td>
                  <td>{humanize(item.cutStatus, "Not applicable")}</td>
                  <td>{formatMoney(item.lineTotal, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="admin-order-manage__empty-inline">
          No order lines are available.
        </div>
      )}
    </section>
  );
}

function CustomerPanel({ order }) {
  const address = getPrimaryAddress(order);
  const addressLines = [
    address.fullName,
    address.line1,
    address.line2,
    address.city,
    address.postalCode,
    address.country,
  ].filter(Boolean);

  return (
    <section className="admin-order-manage__panel">
      <header>
        <div>
          <h2>Customer</h2>
          <p>Contact and fulfilment information.</p>
        </div>
      </header>
      <div className="admin-order-manage__detail-list">
        <div>
          <span>Name</span>
          <strong>{getCustomerName(order) || "Customer"}</strong>
        </div>
        <div>
          <span>Email</span>
          <strong>{order.customer?.email || "-"}</strong>
        </div>
        <div>
          <span>Phone</span>
          <strong>{order.customer?.phone || "-"}</strong>
        </div>
        <div>
          <span>Address</span>
          <strong>{addressLines.length ? addressLines.join(", ") : "-"}</strong>
        </div>
      </div>
    </section>
  );
}

export default function AdminOrderManagementScreen() {
  const { orderNumber = "" } = useParams();
  const dispatch = useDispatch();
  const adminState = useSelector((state) => state.adminOrder || {});
  const user = useSelector((state) => state.userAuth?.userInfo || {});
  const financialPermission = hasFinancialPermission(user);
  const details = adminState.details || {};
  const order = details.order || null;
  const displayOrderNumber = getOrderNumber(order) || safeText(orderNumber);
  const currency = getOrderCurrency(order || {});

  const [paymentStatus, setPaymentStatus] = useState("");

  const busy = Boolean(
    details.loading ||
      adminState.updateStatus?.loading ||
      adminState.updateFulfillment?.loading ||
      adminState.refund?.loading ||
      adminState.manualPayment?.loading,
  );

  const loadOrder = useCallback(async () => {
    if (!safeText(orderNumber)) return;

    try {
      await dispatch(getAdminOrderByNumber(orderNumber));
    } catch (error) {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not load this order right now. Please try again later.",
        ),
      );
    }
  }, [dispatch, orderNumber]);

  useEffect(() => {
    loadOrder();

    return () => {
      dispatch(resetAdminOrderError());
      dispatch(resetAdminOrderMeta());
    };
  }, [dispatch, loadOrder]);

  useEffect(() => {
    if (!order) return;

    setPaymentStatus(normalizeLower(order.payment?.status) || "not_started");
  }, [order]);

  const hasTextileLines = useMemo(
    () =>
      getOrderItems(order || {}).some(
        (item) =>
          normalizeLower(item.productType) === "textile" ||
          normalizeLower(item.cutStatus),
      ),
    [order],
  );

  const savePaymentStatus = useCallback(async () => {
    try {
      await dispatch(
        updateAdminManualPaymentStatus({
          orderNumber: displayOrderNumber,
          status: paymentStatus,
          amountCaptured: order?.grandTotal,
        }),
      );
      toast.success("Payment status updated.");
      await loadOrder();
    } catch (error) {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not update payment right now. Please try again later.",
        ),
      );
    }
  }, [dispatch, displayOrderNumber, loadOrder, order, paymentStatus]);

  if (details.loading && !order) {
    return (
      <main className="admin-order-manage">
        <section className="admin-order-manage__loader">
          <Loader2 className="order-detail__spin" />
        </section>
      </main>
    );
  }

  if (!order) {
    return (
      <main className="admin-order-manage">
        <Meta title="Manage order | Academy Costumes London" robots="noindex, nofollow" />
        <Link to="/admin/orders" className="admin-order-manage__back">
          <ArrowLeft size={18} />
          <span>Back to orders</span>
        </Link>
        <section className="admin-order-manage__empty">
          <PackageCheck size={34} />
          <h2>Order unavailable</h2>
          <p>{details.error || "This order could not be loaded."}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-order-manage">
      <Meta
        title={`Manage ${displayOrderNumber} | Academy Costumes London`}
        robots="noindex, nofollow"
      />

      <Link to="/admin/orders" className="admin-order-manage__back">
        <ArrowLeft size={18} />
        <span>Back to orders</span>
      </Link>

      <header className="admin-order-manage__hero">
        <div>
          <p className="admin-order-manage__eyebrow">Order management</p>
          <h1>{displayOrderNumber}</h1>
          <p className="admin-order-manage__subtitle">
            {getCustomerName(order) || "Customer"} ·{" "}
            {formatDate(order.createdAt || order.placedAt)} ·{" "}
            {formatMoney(order.grandTotal, currency)}
          </p>
        </div>

        <div className="admin-order-manage__hero-actions">
          <button
            type="button"
            className="admin-order-manage__ghost-btn"
            onClick={loadOrder}
            disabled={busy}
          >
            {details.loading ? (
              <Loader2 size={16} className="order-detail__spin" />
            ) : (
              <RefreshCw size={16} />
            )}
            <span>Refresh</span>
          </button>
          <Link
            className="admin-order-manage__ghost-btn"
            to={`/admin/orders/${encodeURIComponent(displayOrderNumber)}/print`}
          >
            <Printer size={16} />
            <span>Print</span>
          </Link>
          <Link
            className="admin-order-manage__dark-btn"
            to={`/admin/orders/${encodeURIComponent(displayOrderNumber)}/picking-slip`}
          >
            <FileText size={16} />
            <span>Picking slip</span>
          </Link>
        </div>
      </header>

      <section className="admin-order-manage__summary-grid">
        <DetailCard
          label="Order"
          value={<StatusBadge value={order.status} />}
          note={displayOrderNumber}
        />
        <DetailCard
          label="Payment"
          value={
            <StatusBadge
              value={order.payment?.status}
              fallback="Unpaid"
              type="payment"
            />
          }
          note={getPaymentLabel(order)}
        />
        <DetailCard
          label="Fulfilment"
          value={
            <StatusBadge
              value={order.fulfillment?.status}
              fallback="Processing"
              type="fulfillment"
            />
          }
          note={humanize(order.fulfillment?.method, "Delivery")}
        />
        <DetailCard
          label="Total"
          value={formatMoney(order.grandTotal, currency)}
          note={`${getOrderItems(order).length} line(s)`}
        />
      </section>

      <div className="admin-order-manage__grid">
        <AdminOrderOperationalActions order={order} onRefresh={loadOrder} busy={busy} />
        <OrderTimeline order={order} />
        {financialPermission && !["stripe", "paypal", "sumup"].includes(order.payment?.provider) ? <section className="admin-order-manage__panel">
          <header>
            <div>
              <h2>Manual payment</h2>
            </div>
          </header>

          <div className="admin-order-manage__form-grid">
            <FieldSelect
              label="Payment"
              value={paymentStatus}
              options={PAYMENT_STATUSES}
              onChange={setPaymentStatus}
              disabled={busy || !financialPermission || ["stripe", "paypal", "sumup"].includes(order.payment?.provider)}
            />
            <button
              type="button"
              className="admin-order-manage__dark-btn"
              onClick={savePaymentStatus}
              disabled={
                busy || !financialPermission || ["stripe", "paypal", "sumup"].includes(order.payment?.provider) || !PAYMENT_STATUSES.includes(paymentStatus) || paymentStatus === normalizeLower(order.payment?.status)
              }
            >
              <Save size={16} />
              <span>Save payment</span>
            </button>
          </div>
        </section> : null}

        <CustomerPanel order={order} />
        {financialPermission ? <AdminOrderFinancialActions order={order} onRefresh={loadOrder} busy={busy} /> : null}

        <ReceiptSummary order={order} />

        {hasTextileLines ? (
          <section className="admin-order-manage__panel">
            <header>
              <div>
                <h2>Textile cutting</h2>
                <p>Use the picking slip to manage individual textile line cuts.</p>
              </div>
              <Scissors size={20} />
            </header>
            <Link
              className="admin-order-manage__dark-btn"
              to={`/admin/orders/${encodeURIComponent(
                displayOrderNumber,
              )}/picking-slip`}
            >
              Open picking slip
            </Link>
          </section>
        ) : null}

        <OrderLines order={order} />
        {order.events?.length ? <section className="admin-order-manage__panel admin-order-manage__panel--wide">
          <header><h2>Order history</h2></header>
          <ul>{order.events.map((event, index) => <li key={event._id || index}>
            {formatDate(event.createdAt)} · {event.message}{event.metadata?.reason ? ` Reason: ${event.metadata.reason}` : ""}
          </li>)}</ul>
        </section> : null}
      </div>
    </main>
  );
}
